import {createContext, Dispatch, SetStateAction, useEffect, useState} from 'react'
import qs from 'qs'
import {ID, QueryResponseContextProps, QueryState} from './models'

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState)
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== ''
}

// Example: page=1&items_per_page=10&sort=id&order=desc&search=a&filter_name=a&filter_online=false
function stringifyRequestQuery(state: QueryState): string {
  const paginationData = {
    page: state.page,
    items_per_page: state.items_per_page,
  }
  const pagination = qs.stringify(paginationData, {
    filter: ['page', 'items_per_page'],
    skipNulls: true,
  })
  const sort = qs.stringify(state, {filter: ['sort', 'order'], skipNulls: true})
  const search = isNotEmpty(state.search)
    ? qs.stringify(state, {filter: ['search'], skipNulls: true})
    : ''
  const startDate = isNotEmpty(state.start_date)
    ? qs.stringify(state, {filter: ['start_date'], skipNulls: true})
    : null
  const endDate = isNotEmpty(state.end_date)
    ? qs.stringify(state, {filter: ['end_date'], skipNulls: true})
    : null
  const date = isNotEmpty(state.date)
    ? qs.stringify(state, {filter: ['date'], skipNulls: true})
    : null

  const user = isNotEmpty(state.user)
    ? qs.stringify(state, {filter: ['user'], skipNulls: true})
    : null

  const destinationCountry = isNotEmpty(state.destination_country)
    ? qs.stringify(state, {filter: ['destination_country'], skipNulls: true})
    : null

  const cityId = isNotEmpty(state.city_id)
    ? qs.stringify(state, {filter: ['city_id'], skipNulls: true})
    : null

  const category = isNotEmpty(state.category)
    ? qs.stringify(state, {filter: ['category'], skipNulls: true})
    : null

  const transferType = isNotEmpty(state.transfer_type)
    ? qs.stringify(state, {filter: ['transfer_type'], skipNulls: true})
    : null

  const agentCountry = isNotEmpty(state.agent_country)
    ? qs.stringify(state, {filter: ['agent_country'], skipNulls: true})
    : null

  const agentCity = isNotEmpty(state.agent_city)
    ? qs.stringify(state, {filter: ['agent_city'], skipNulls: true})
    : null

  const agentCategory = isNotEmpty(state.agent_category)
    ? qs.stringify(state, {filter: ['agent_category'], skipNulls: true})
    : null

  const agentType = isNotEmpty(state.agent_type)
    ? qs.stringify(state, {filter: ['agent_type'], skipNulls: true})
    : null

  const assignTo = isNotEmpty(state.assign_to)
    ? qs.stringify(state, {filter: ['assign_to'], skipNulls: true})
    : null

  const isAccepted = isNotEmpty(state.is_accepted)
    ? qs.stringify(state, {filter: ['is_accepted'], skipNulls: true})
    : null

  const filter = state.filter
    ? Object.entries(state.filter as Object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => {
          return `filter_${obj[0]}=${obj[1]}`
        })
        .join('&')
    : ''

  return [
    pagination,
    sort,
    search,
    filter,
    startDate,
    endDate,
    user,
    date,
    destinationCountry,
    cityId,
    category,
    transferType,
    agentCountry,
    agentCity,
    agentCategory,
    agentType,
    assignTo,
    isAccepted,
  ]
    .filter((f) => f)
    .join('&')
    .toLowerCase()
}

function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query)
  return cache as QueryState
}

function calculatedGroupingIsDisabled<T>(isLoading: boolean, data: Array<T> | undefined): boolean {
  if (isLoading) {
    return true
  }

  return !data || !data.length
}

function calculateIsAllDataSelected<T>(data: Array<T> | undefined, selected: Array<ID>): boolean {
  if (!data) {
    return false
  }

  return data.length > 0 && data.length === selected.length
}

function groupingOnSelect(
  id: ID,
  selected: Array<ID>,
  setSelected: Dispatch<SetStateAction<Array<ID>>>
) {
  if (!id) {
    return
  }

  if (selected.includes(id)) {
    setSelected(selected.filter((itemId) => itemId !== id))
  } else {
    const updatedSelected = [...selected]
    updatedSelected.push(id)
    setSelected(updatedSelected)
  }
}

function groupingOnSelectAll<T>(
  isAllSelected: boolean,
  setSelected: Dispatch<SetStateAction<Array<ID>>>,
  data?: Array<T & {id?: ID}>
) {
  if (isAllSelected) {
    setSelected([])
    return
  }

  if (!data || !data.length) {
    return
  }

  setSelected(data.filter((item) => item.id).map((item) => item.id))
}

// Hook
function useDebounce(value: string | undefined, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay] // Only re-call effect if value or delay changes
  )
  return debouncedValue
}

export {
  createResponseContext,
  stringifyRequestQuery,
  parseRequestQuery,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  groupingOnSelect,
  groupingOnSelectAll,
  useDebounce,
  isNotEmpty,
}
